import {
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { shuffled } from "@postdaily/api/lib/shuffled";
import type { Outputs } from "@postdaily/api/routers/index";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@postdaily/ui/components/alert-dialog";
import { Button } from "@postdaily/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@postdaily/ui/components/dropdown-menu";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@postdaily/ui/components/empty";
import { Input } from "@postdaily/ui/components/input";
import { Separator } from "@postdaily/ui/components/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@postdaily/ui/components/sheet";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import { Toggle } from "@postdaily/ui/components/toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@postdaily/ui/components/tooltip";
import { cn } from "@postdaily/ui/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  Check,
  ChevronsUpDown,
  Clock,
  GripVertical,
  Pause,
  PenLine,
  Pin,
  PinOff,
  Play,
  PlugZap,
  Plus,
  Shuffle,
  SkipForward,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  XIcon,
} from "lucide-react";
import { parseAsString, useQueryStates } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ChannelAvatar } from "@/components/channels/channel-avatar";
import { useComposer } from "@/components/composer/composer-dialog-provider";
import { PageHeader } from "@/components/page-header";
import { ProviderIcon } from "@/components/social-accounts/provider-icon";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { track } from "@/lib/analytics";
import { getChannelLabel, getChannelSecondaryLabel } from "@/lib/channel-label";
import { getProviderLabel } from "@/lib/provider-labels";
import {
  buildQueueTimeline,
  cardPreview,
  firstLine,
  inZone,
  projectOccurrences,
  type QueueChannel,
  type QueuePost,
  type TimelineRow,
} from "@/lib/queue-timeline";
import { useTRPC } from "@/utils/trpc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const Route = createFileRoute("/(app)/queue")({
  component: RouteComponent,
});

// --- Types (bound to the real tRPC output shapes) ---------------------------

type QueueSlot = Outputs["queue"]["slots"]["list"][number];
type QueueCategory = Outputs["queue"]["categories"]["list"][number];
type QueueTimeline = Outputs["queue"]["timeline"];
type QueueEntryList = QueueTimeline["entries"];
type QueueSkipList = QueueTimeline["skips"];
type SocialAccount = Outputs["socialAccounts"]["list"][number];

type DayGroup = {
  key: string;
  ms: number;
  label: string;
  date: string;
  sub: string;
  rows: TimelineRow[];
};

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const DAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"] as const;
const WEEKDAY_DAYS = [1, 2, 3, 4, 5] as const;
const WEEKEND_DAYS = [0, 6] as const;
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

function sameDaySet(a: readonly number[], b: readonly number[]) {
  if (a.length !== b.length) {
    return false;
  }
  const set = new Set(a);
  return b.every((day) => set.has(day));
}

/** Category colors fall back to the theme chart tokens (deterministic by list
 *  index) when a category has no stored color. */
const CHART_TOKENS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

// --- Time helpers -----------------------------------------------------------

function dayLabel(ms: number, tz: string) {
  const day = inZone(ms, tz).startOf("day");
  const today = inZone(Date.now(), tz).startOf("day");
  const diff = day.diff(today, "day");
  if (diff === 0) {
    return "Today";
  }
  if (diff === 1) {
    return "Tomorrow";
  }
  return inZone(ms, tz).format("dddd");
}

function timeParts(ms: number, tz: string) {
  const at = inZone(ms, tz);
  return { time: at.format("h:mm"), ampm: at.format("A") };
}

function whenLabel(ms: number, tz: string) {
  return `${dayLabel(ms, tz)} · ${inZone(ms, tz).format("h:mm A")}`;
}

function minuteLabel(minuteOfDay: number) {
  return dayjs()
    .hour(Math.floor(minuteOfDay / 60))
    .minute(minuteOfDay % 60)
    .format("h:mm A");
}

function channelHandle(channel: QueueChannel) {
  const handle =
    channel.providerAccountHandle ?? channel.displayName ?? channel.provider;
  return handle.startsWith("@") ? handle : `@${handle}`;
}

/**
 * Whether a queued post can actually be published: the publish gate rejects any
 * target whose account is not `active`, so a post pointing at a disconnected (or
 * reauth-pending) channel counts down to a failure, not a publish. Disconnecting
 * now drains the channel's queue, so this only fires for posts queued before
 * that shipped, or for a cross-posted post that lost one of its channels.
 */
function channelIssue(channels: QueueChannel[]) {
  if (channels.some((channel) => channel.disconnectedAt !== null)) {
    return "disconnected" as const;
  }
  if (channels.some((channel) => channel.status !== "active")) {
    return "reconnect" as const;
  }
  return null;
}

const CHANNEL_ISSUE_LABEL = {
  disconnected: "Channel disconnected",
  reconnect: "Reconnect needed",
} as const;

/** "@handle" for one channel, or "@handle +N" when a post targets several. */
function channelSummaryLabel(channels: QueueChannel[]) {
  const [first] = channels;
  if (!first) {
    return "";
  }
  const extra = channels.length - 1;
  return extra > 0 ? `${channelHandle(first)} +${extra}` : channelHandle(first);
}

// --- Optimistic cache helpers (mirror the server's reorder/skip semantics) ---

/** Moves `postId` to `toIndex`, matching the server's remove-then-insert. */
function reorderEntries(
  entries: QueueEntryList,
  postId: string,
  toIndex: number,
): QueueEntryList {
  const next = [...entries];
  const from = next.findIndex((entry) => entry.postId === postId);
  if (from === -1) {
    return entries;
  }
  const [moved] = next.splice(from, 1);
  if (!moved) {
    return entries;
  }
  next.splice(Math.max(0, Math.min(toIndex, next.length)), 0, moved);
  return next;
}

function shuffleEntries(entries: QueueEntryList): QueueEntryList {
  return shuffled(entries);
}

function addSkip(skips: QueueSkipList, slotAt: Date): QueueSkipList {
  const ms = slotAt.getTime();
  if (skips.some((skip) => new Date(skip.slotAt).getTime() === ms)) {
    return skips;
  }
  return [...skips, { slotAt }];
}

function removeSkip(skips: QueueSkipList, slotAt: Date): QueueSkipList {
  const ms = slotAt.getTime();
  return skips.filter((skip) => new Date(skip.slotAt).getTime() !== ms);
}

/**
 * Recomputes each entry's `assignedSlotAt` by projecting the scope's grid so a
 * dragged/shuffled card moves instantly. Only safe when the whole view is a
 * single grid (a channel with its own slots): then the shown entries are exactly
 * that grid's posts, so the projection matches what the server reflow will do.
 * All-channels / mixed-grid views fall back to array-only reorder (the card
 * settles when the server reflow returns on settle).
 */
function withOptimisticTimes(
  data: QueueTimeline,
  entries: QueueEntryList,
): QueueTimeline {
  const grid = data.scope.gridAccountId;
  const singleGrid =
    grid !== null && entries.every((entry) => entry.gridAccountId === grid);
  // Pinned posts hold custom times and the rest reflow around them — too fiddly
  // to mirror here; let the server reflow reconcile on settle. While paused the
  // server holds publish times entirely, so recomputing them would show times
  // the reflow will never apply.
  const hasPins = entries.some((entry) => entry.pinnedAt != null);
  if (!singleGrid || hasPins || data.scope.paused != null) {
    return { ...data, entries };
  }
  const skipped = new Set(
    data.skips.map((skip) => new Date(skip.slotAt).getTime()),
  );
  const occurrences = projectOccurrences(
    data.slots,
    data.scope.timezone,
    entries.length,
    new Date(),
    skipped,
  );
  return {
    ...data,
    entries: entries.map((entry, index) => {
      const ms = occurrences[index];
      return ms === undefined
        ? entry
        : { ...entry, assignedSlotAt: new Date(ms) };
    }),
  };
}

function reorderTimeline(
  data: QueueTimeline,
  postId: string,
  toIndex: number,
): QueueTimeline {
  return withOptimisticTimes(
    data,
    reorderEntries(data.entries, postId, toIndex),
  );
}

function shuffleTimeline(data: QueueTimeline): QueueTimeline {
  return withOptimisticTimes(data, shuffleEntries(data.entries));
}

/** Optimistically pin a post to a custom instant (drag onto an open slot). */
function pinTimeline(
  data: QueueTimeline,
  postId: string,
  slotMs: number,
): QueueTimeline {
  const at = new Date(slotMs);
  return {
    ...data,
    entries: data.entries.map((entry) =>
      entry.postId === postId
        ? { ...entry, pinnedAt: at, assignedSlotAt: at }
        : entry,
    ),
  };
}

/** Optimistically clear a post's pin (the server reflow re-slots it). */
function unpinTimeline(data: QueueTimeline, postId: string): QueueTimeline {
  return {
    ...data,
    entries: data.entries.map((entry) =>
      entry.postId === postId ? { ...entry, pinnedAt: null } : entry,
    ),
  };
}

// --- Channel labels ---------------------------------------------------------

function accountName(account: SocialAccount) {
  return getChannelLabel(account);
}

// --- Shared mutation handlers / stable fallbacks ----------------------------

const onMutationError = (error: { message: string }) =>
  toast.error(error.message);

/** Stable empty fallbacks so derived memos don't see a new array each render. */
const EMPTY_CATEGORIES: QueueCategory[] = [];
const EMPTY_ENTRIES: QueueEntryList = [];

/**
 * Pointer drops resolve by pointer position; keyboard drags have no pointer, so
 * fall back to rect intersection — without this, KeyboardSensor drags can never
 * land on a droppable row.
 */
const dropCollisionDetection: CollisionDetection = (args) => {
  const withPointer = pointerWithin(args);
  return withPointer.length > 0 ? withPointer : rectIntersection(args);
};

// --- Route ------------------------------------------------------------------

function RouteComponent() {
  const { currentWorkspace } = useCurrentWorkspace();

  if (!currentWorkspace) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="size-10" />
      </div>
    );
  }

  return (
    <QueueManager
      workspaceId={currentWorkspace.id}
      timezone={currentWorkspace.defaultTimezone}
    />
  );
}

function QueueManager({
  workspaceId,
  timezone: workspaceTz,
}: {
  workspaceId: string;
  timezone: string;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { openComposer } = useComposer();

  // Selected channel scope (?channel=<id>); empty = "All channels".
  const [{ channel }, setFilters] = useQueryStates({
    channel: parseAsString.withDefault(""),
  });
  const selectedChannelId = channel || null;

  const accounts = useQuery(
    trpc.socialAccounts.list.queryOptions({ workspaceId }),
  );
  const categories = useQuery(
    trpc.queue.categories.list.queryOptions({ workspaceId }),
  );

  // The whole timeline for the current scope in one round-trip: null
  // socialAccountId = All channels; a channel id = that channel's per-channel
  // view (its posts, its resolved grid's slots/skips/timezone, its pause state).
  const timelineInput = { workspaceId, socialAccountId: selectedChannelId };
  const timeline = useQuery(trpc.queue.timeline.queryOptions(timelineInput));
  const timelineKey = trpc.queue.timeline.queryKey(timelineInput);

  const data = timeline.data;
  const scope = data?.scope;
  // The grid the current view schedules on (channel's own grid when it has one,
  // else the workspace-shared grid) and the timezone that grid publishes in.
  const gridAccountId = scope?.gridAccountId ?? null;
  const tz = scope?.timezone ?? workspaceTz;

  // Whether the Settings slide-over is open. Declared here (above the slots
  // query) so the query can be gated on it — the sheet is rarely opened, so
  // there's no reason to fetch its slots on every queue page load.
  const [settingsOpen, setSettingsOpen] = useState(false);

  // The Settings sheet edits the SAME grid the timeline shows. Only fetched
  // while the sheet is open.
  const settingsSlots = useQuery(
    trpc.queue.slots.list.queryOptions(
      {
        workspaceId,
        socialAccountId: gridAccountId,
      },
      { enabled: settingsOpen },
    ),
  );

  // Queue mutations can only change data under the queue router (timeline,
  // entries, slots, skips, status, categories) and post lists (calendar/list
  // view move between draft/scheduled). Scope invalidation to those two routers
  // rather than blowing away every active query in the app (session bootstrap,
  // notifications, sidebar, onboarding, …). The post lists are inactive on this
  // route, so this only marks them stale — they refetch on next mount.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.queue.pathKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.posts.pathKey() }),
    ]);
  // Settings mutations (simple invalidate-on-success).
  const createCategory = useMutation(
    trpc.queue.categories.create.mutationOptions({
      onSuccess: invalidate,
      onError: onMutationError,
    }),
  );
  const archiveCategory = useMutation(
    trpc.queue.categories.archive.mutationOptions({
      onSuccess: invalidate,
      onError: onMutationError,
    }),
  );
  const createSlot = useMutation(
    trpc.queue.slots.create.mutationOptions({
      onSuccess: invalidate,
      onError: onMutationError,
    }),
  );
  const deleteSlot = useMutation(
    trpc.queue.slots.delete.mutationOptions({
      onSuccess: invalidate,
      onError: onMutationError,
    }),
  );

  // Queue mutations — optimistic patch of the single timeline query, rollback on
  // error, reconcile on settle. The reflow that moves real publish times happens
  // server-side. Reorder/shuffle recompute times optimistically in single-grid
  // (per-channel) views so the card moves instantly (see withOptimisticTimes).
  const cancelTimeline = () =>
    queryClient.cancelQueries({ queryKey: timelineKey });
  const snapshotTimeline = () =>
    queryClient.getQueryData<QueueTimeline>(timelineKey);
  const rollbackTimeline = (previous: QueueTimeline | undefined) => {
    if (previous) {
      queryClient.setQueryData(timelineKey, previous);
    }
  };
  const patchTimeline = (updater: (data: QueueTimeline) => QueueTimeline) => {
    queryClient.setQueryData<QueueTimeline>(timelineKey, (old) =>
      old ? updater(old) : old,
    );
  };

  const removeMutation = useMutation(
    trpc.queue.remove.mutationOptions({
      onMutate: async ({ postId }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => ({
          ...d,
          entries: d.entries.filter((entry) => entry.postId !== postId),
        }));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => {
        track("queue_action", { action: "remove" });
        toast.success("Removed from queue — moved to Drafts");
      },
      onSettled: invalidate,
    }),
  );

  // All-channels reorder = global order; per-channel reorder = within the
  // channel's own queue. Both patch the timeline the same way optimistically.
  const reorderMutation = useMutation(
    trpc.queue.reorder.mutationOptions({
      onMutate: async ({ postId, toIndex }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => reorderTimeline(d, postId, toIndex));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => track("queue_action", { action: "reorder" }),
      onSettled: invalidate,
    }),
  );

  const reorderChannelMutation = useMutation(
    trpc.queue.channel.reorder.mutationOptions({
      onMutate: async ({ postId, toIndex }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => reorderTimeline(d, postId, toIndex));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => track("queue_action", { action: "reorder" }),
      onSettled: invalidate,
    }),
  );

  const shuffleMutation = useMutation(
    trpc.queue.shuffle.mutationOptions({
      onMutate: async () => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => shuffleTimeline(d));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => track("queue_action", { action: "shuffle" }),
      onSettled: invalidate,
    }),
  );

  const shuffleChannelMutation = useMutation(
    trpc.queue.channel.shuffle.mutationOptions({
      onMutate: async () => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => shuffleTimeline(d));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => track("queue_action", { action: "shuffle" }),
      onSettled: invalidate,
    }),
  );

  const skipMutation = useMutation(
    trpc.queue.skipSlot.mutationOptions({
      onMutate: async ({ slotAt, socialAccountId }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        // Only render a skipped row when the skip lands on the grid this view
        // shows; a skip on another post's grid just moves that post (the card
        // settles on refetch).
        patchTimeline((d) =>
          (d.scope.gridAccountId ?? null) === (socialAccountId ?? null)
            ? { ...d, skips: addSkip(d.skips, slotAt) }
            : d,
        );
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => track("queue_action", { action: "skip" }),
      onSettled: invalidate,
    }),
  );

  const restoreMutation = useMutation(
    trpc.queue.restoreSlot.mutationOptions({
      onMutate: async ({ slotAt }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => ({ ...d, skips: removeSkip(d.skips, slotAt) }));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => track("queue_action", { action: "restore" }),
      onSettled: invalidate,
    }),
  );

  // Pin a post to a custom time (drag onto an open slot); unpin returns it to the
  // queue's rank-based placement.
  const pinMutation = useMutation(
    trpc.queue.pinToSlot.mutationOptions({
      onMutate: async ({ postId, slotAt }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => pinTimeline(d, postId, slotAt.getTime()));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => {
        track("queue_action", { action: "pin" });
        toast.success("Pinned to a custom time");
      },
      onSettled: invalidate,
    }),
  );

  const unpinMutation = useMutation(
    trpc.queue.unpin.mutationOptions({
      onMutate: async ({ postId }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => unpinTimeline(d, postId));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: () => track("queue_action", { action: "unpin" }),
      onSettled: invalidate,
    }),
  );

  // "Customize for this channel" — detach an inheriting channel from the default.
  const customizeMutation = useMutation(
    trpc.queue.slots.customize.mutationOptions({
      onSuccess: () => {
        invalidate();
        toast.success("This channel now has its own schedule");
      },
      onError: onMutationError,
    }),
  );

  const pauseMutation = useMutation(
    trpc.queue.setPaused.mutationOptions({
      onMutate: async ({ paused: nextPaused }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => ({
          ...d,
          scope: { ...d.scope, paused: nextPaused ? new Date() : null },
        }));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: (result) =>
        track("queue_action", { action: result.paused ? "pause" : "resume" }),
      onSettled: invalidate,
    }),
  );

  const pauseChannelMutation = useMutation(
    trpc.queue.channel.setPaused.mutationOptions({
      onMutate: async ({ paused: nextPaused }) => {
        await cancelTimeline();
        const previous = snapshotTimeline();
        patchTimeline((d) => ({
          ...d,
          scope: { ...d.scope, paused: nextPaused ? new Date() : null },
        }));
        return { previous };
      },
      onError: (error, _vars, ctx) => {
        rollbackTimeline(ctx?.previous);
        onMutationError(error);
      },
      onSuccess: (result) =>
        track("queue_action", { action: result.paused ? "pause" : "resume" }),
      onSettled: invalidate,
    }),
  );

  // --- Local UI state (view-only; all data state is server state) -----------
  // The post currently being dragged for reorder + its source width (so the
  // drag-overlay preview matches the card it lifted from).
  const [active, setActive] = useState<{ id: string; width: number } | null>(
    null,
  );

  // Settings form state — default to weekdays so one click adds a full work-week.
  const [categoryName, setCategoryName] = useState("");
  const [slotDays, setSlotDays] = useState<number[]>([...WEEKDAY_DAYS]);
  const [slotTime, setSlotTime] = useState("09:00");

  // Coarse clock so passed slots drop off and day rollover updates. The live
  // per-second hero countdown ticks in its own component to avoid re-rendering
  // the whole timeline every second.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // A deep link can carry a stale/foreign channel id (disconnected channel,
  // pasted URL). Once accounts are known, fall back to "All channels" instead
  // of erroring the timeline query forever.
  const accountsData = accounts.data;
  useEffect(() => {
    if (
      accountsData &&
      selectedChannelId &&
      !accountsData.some((account) => account.id === selectedChannelId)
    ) {
      void setFilters({ channel: "" });
    }
  }, [accountsData, selectedChannelId, setFilters]);

  // --- Derived data ---------------------------------------------------------

  const categoryList = categories.data ?? EMPTY_CATEGORIES;
  const categoryMeta = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    categoryList.forEach((category: QueueCategory, index) => {
      map.set(category.id, {
        name: category.name,
        color: category.color ?? CHART_TOKENS[index % CHART_TOKENS.length],
      });
    });
    return map;
  }, [categoryList]);

  // Entries + slots + skips come from the one timeline query (already scoped to
  // the selected channel and its resolved grid, active slots only).
  const timelineEntries = data?.entries ?? EMPTY_ENTRIES;

  const activeSlots = useMemo(
    () =>
      (data?.slots ?? []).map((slot) => ({
        dayOfWeek: slot.dayOfWeek,
        minuteOfDay: slot.minuteOfDay,
      })),
    [data?.slots],
  );

  // Queue order is the server's persisted rank (entries come back ordered).
  const order = useMemo(
    () => timelineEntries.map((entry) => entry.postId),
    [timelineEntries],
  );

  const postsById = useMemo(() => {
    const map = new Map<string, QueuePost>();
    for (const entry of timelineEntries) {
      const postTitle = entry.postTitle?.trim();
      const postText = entry.postText ?? "";
      map.set(entry.postId, {
        id: entry.postId,
        title: postTitle || firstLine(postText) || "Untitled draft",
        text: cardPreview(postTitle, postText),
        categoryId: entry.categoryId,
        postId: entry.postId,
        channels: entry.channels,
        gridAccountId: entry.gridAccountId,
      });
    }
    return map;
  }, [timelineEntries]);

  const skippedSet = useMemo(
    () =>
      new Set(
        (data?.skips ?? []).map((skip) => new Date(skip.slotAt).getTime()),
      ),
    [data?.skips],
  );

  const paused = Boolean(scope?.paused);

  const { rows, firstFilledKey, filledCount, openCount } = useMemo(
    () =>
      buildQueueTimeline({
        entries: timelineEntries,
        postsById,
        activeSlots,
        timezone: tz,
        skipped: skippedSet,
        now: nowTick,
      }),
    [timelineEntries, postsById, activeSlots, tz, skippedSet, nowTick],
  );

  const days = useMemo<DayGroup[]>(() => {
    const groups: DayGroup[] = [];
    const byKey = new Map<string, DayGroup>();
    for (const row of rows) {
      const dkey = inZone(row.ms, tz).format("YYYY-MM-DD");
      let group = byKey.get(dkey);
      if (!group) {
        group = {
          key: dkey,
          ms: row.ms,
          label: dayLabel(row.ms, tz),
          date: inZone(row.ms, tz).format("MMM D"),
          sub: "",
          rows: [],
        };
        byKey.set(dkey, group);
        groups.push(group);
      }
      group.rows.push(row);
    }
    for (const group of groups) {
      const filled = group.rows.filter((r) => r.kind === "filled").length;
      const open = group.rows.filter((r) => r.kind === "open").length;
      const parts: string[] = [];
      if (filled) {
        parts.push(`${filled} ${filled === 1 ? "post" : "posts"}`);
      }
      if (open) {
        parts.push(`${open} open`);
      }
      group.sub = parts.join(" · ") || "no slots";
    }
    return groups;
  }, [rows, tz]);

  const nextUpRow = useMemo(
    () => rows.find((row) => row.key === firstFilledKey) ?? null,
    [rows, firstFilledKey],
  );

  const activePost = active ? (postsById.get(active.id) ?? null) : null;

  // --- Actions --------------------------------------------------------------

  const editPost = useCallback(
    (post: QueuePost) => openComposer({ postId: post.postId }),
    [openComposer],
  );

  const removePost = useCallback(
    (post: QueuePost) =>
      removeMutation.mutate({ workspaceId, postId: post.postId }),
    [removeMutation, workspaceId],
  );

  const returnToQueue = useCallback(
    (post: QueuePost) =>
      unpinMutation.mutate({ workspaceId, postId: post.postId }),
    [unpinMutation, workspaceId],
  );

  // Skipping a FILLED slot must target the grid the entry schedules on (its
  // channel's grid for single-channel posts) — a skip recorded on the view's
  // grid would silently do nothing for a post on another grid. Callers pass the
  // post's `gridAccountId`; `undefined` (no post context) falls back to the
  // viewed channel.
  const skipOccurrence = useCallback(
    (ms: number, gridAccountId?: string | null) =>
      skipMutation.mutate({
        workspaceId,
        slotAt: new Date(ms),
        socialAccountId:
          gridAccountId === undefined ? selectedChannelId : gridAccountId,
      }),
    [skipMutation, workspaceId, selectedChannelId],
  );

  const restoreOccurrence = useCallback(
    (ms: number) =>
      restoreMutation.mutate({
        workspaceId,
        slotAt: new Date(ms),
        socialAccountId: selectedChannelId,
      }),
    [restoreMutation, workspaceId, selectedChannelId],
  );

  const togglePause = useCallback(() => {
    const next = !paused;
    if (selectedChannelId) {
      pauseChannelMutation.mutate({
        workspaceId,
        socialAccountId: selectedChannelId,
        paused: next,
      });
    } else {
      pauseMutation.mutate({ workspaceId, paused: next });
    }
  }, [
    paused,
    selectedChannelId,
    pauseChannelMutation,
    pauseMutation,
    workspaceId,
  ]);

  const runShuffle = useCallback(() => {
    if (selectedChannelId) {
      shuffleChannelMutation.mutate({
        workspaceId,
        socialAccountId: selectedChannelId,
      });
    } else {
      shuffleMutation.mutate({ workspaceId });
    }
  }, [selectedChannelId, shuffleChannelMutation, shuffleMutation, workspaceId]);

  const pausePending =
    pauseMutation.isPending || pauseChannelMutation.isPending;
  const shufflePending =
    shuffleMutation.isPending || shuffleChannelMutation.isPending;

  // Require a little travel before a drag starts so clicking a card's title or
  // action buttons still works. KeyboardSensor gives the drag handle a keyboard
  // path (Space/Enter lifts, arrows move, Space drops, Esc cancels).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActive({
      id: String(event.active.id),
      width: event.active.rect.current.initial?.width ?? 0,
    });
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      // SAFETY: dnd-kit types `data.current` as a free-form record. Every
      // droppable on this page sets these three fields (see the slot and card
      // droppables below), and each is checked for presence before it is read.
      const over = event.over?.data.current as
        | { rank?: number; ms?: number; kind?: TimelineRow["kind"] }
        | undefined;
      const postId = String(event.active.id);

      if (over?.kind === "skipped") {
        // A skipped slot publishes nothing — dropping here is a no-op (without
        // this guard the drop falls into the reorder branch and silently sends
        // the post to the end of the queue).
        setActive(null);
        return;
      }

      if (over?.kind === "open" && over.ms !== undefined) {
        // Drop on an OPEN slot → pin the post to that exact time (custom time),
        // even for a single post.
        pinMutation.mutate({ workspaceId, postId, slotAt: new Date(over.ms) });
      } else if (over?.rank !== undefined) {
        // Drop on a FILLED card → reorder. Per-channel view reorders within the
        // channel's own queue; All-channels reorders the global stream.
        if (selectedChannelId) {
          reorderChannelMutation.mutate({
            workspaceId,
            socialAccountId: selectedChannelId,
            postId,
            toIndex: over.rank,
          });
        } else {
          reorderMutation.mutate({ workspaceId, postId, toIndex: over.rank });
        }
      }
      setActive(null);
    },
    [
      pinMutation,
      reorderChannelMutation,
      reorderMutation,
      selectedChannelId,
      workspaceId,
    ],
  );

  const handleDragCancel = useCallback(() => setActive(null), []);

  // Open the full composer prefilled to this exact slot time.
  const openSlotComposer = useCallback(
    (at: number) =>
      openComposer({
        // Pre-select the channel being viewed (null for All channels → the user
        // picks in the composer).
        queueSlot: {
          at: new Date(at),
          socialAccountId: selectedChannelId,
        },
      }),
    [openComposer, selectedChannelId],
  );

  // --- Loading / empty states -----------------------------------------------

  const isLoading = timeline.isPending;
  // Show the "set your posting times" empty state only when there is genuinely
  // nothing to show for this scope: no active slots on the grid AND no queued
  // posts (a channel scheduling purely via its own grid still sees its timeline).
  const hasGridSlots = activeSlots.length > 0;
  const hasQueuedPosts = timelineEntries.length > 0;
  const hasSlots = hasGridSlots || hasQueuedPosts;

  const openSettings = () => setSettingsOpen(true);

  // Settings-sheet scope: which schedule it edits, and whether the selected
  // channel is still inheriting the default (→ read-only + "Customize").
  const selectedAccount = selectedChannelId
    ? (accounts.data ?? []).find((account) => account.id === selectedChannelId)
    : undefined;
  const settingsScopeName = selectedAccount
    ? accountName(selectedAccount)
    : null;
  const settingsInherited = (settingsSlots.data ?? []).some(
    (slot) => slot.inherited,
  );

  // Posts whose target channel can no longer publish (disconnected / needs
  // reauth). They still hold their slot, so they are counted — but the header
  // must not advertise a queue that is quietly going to fail.
  const { blockedCount, blockedIssue } = useMemo(() => {
    const issues = timelineEntries
      .map((entry) => channelIssue(entry.channels))
      .filter((issue) => issue !== null);
    return {
      blockedCount: issues.length,
      blockedIssue: issues.includes("disconnected")
        ? ("disconnected" as const)
        : ("reconnect" as const),
    };
  }, [timelineEntries]);
  const hasConnectedChannels = (accounts.data ?? []).length > 0;

  const summary = hasConnectedChannels
    ? `${filledCount} ${
        filledCount === 1 ? "post" : "posts"
      } queued · ${openCount} open ${openCount === 1 ? "slot" : "slots"}`
    : `${filledCount} ${
        filledCount === 1 ? "post" : "posts"
      } queued · no channels connected`;

  const tailNote = !hasConnectedChannels
    ? "No channels are connected, so nothing will publish. Connect a channel to start the queue."
    : openCount > 0
      ? `You have ${openCount} open ${
          openCount === 1 ? "slot" : "slots"
        } coming up. Add posts or new posting times to keep the queue full.`
      : "Every upcoming slot is filled. Add more posting times to schedule further ahead.";

  return (
    <div className="mx-auto flex w-full max-w-[880px] flex-col gap-5 md:gap-6">
      <PageHeader
        title="Queue"
        description="Your posts, lined up to publish at the next open time."
        actions={
          <Button size="sm" onClick={() => openComposer()}>
            <PenLine data-icon="inline-start" />
            New post
          </Button>
        }
      />

      {/* 1. Context toolbar — always rendered so the channel switcher + actions
          stay put while only the timeline body swaps to skeletons on load.
          Stack on narrow viewports so the switcher never overflows into the
          action buttons (flex-1 + min-w-0 was shrinking the left group under
          its content and causing visual overlap). */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2.5">
          <ChannelSwitcher
            accounts={accounts.data ?? []}
            selectedChannelId={selectedChannelId}
            onSelect={(id) => void setFilters({ channel: id ?? "" })}
          />
          <span className="hidden min-w-0 truncate text-muted-foreground text-sm md:inline">
            {isLoading ? "Loading…" : summary}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={isLoading || pausePending}
            onClick={togglePause}
          >
            {paused ? (
              <>
                <Play
                  data-icon="inline-start"
                  className="fill-emerald-500 text-emerald-500"
                />
                <span className="text-emerald-500">
                  <span className="sm:hidden">Resume</span>
                  <span className="hidden sm:inline">Resume queue</span>
                </span>
              </>
            ) : (
              <>
                <Pause data-icon="inline-start" className="fill-current" />
                Pause
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={isLoading || shufflePending || order.length < 2}
            onClick={runShuffle}
          >
            <Shuffle data-icon="inline-start" />
            Shuffle
          </Button>
          <Button variant="outline" size="sm" onClick={openSettings}>
            <SlidersHorizontal data-icon="inline-start" />
            Settings
          </Button>
        </div>
      </div>

      {isLoading ? (
        <QueueBodySkeleton />
      ) : timeline.isError ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Couldn’t load your queue</EmptyTitle>
            <EmptyDescription>
              {timeline.error.message ||
                "Something went wrong loading the timeline."}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => void timeline.refetch()}>Try again</Button>
          </EmptyContent>
        </Empty>
      ) : !hasSlots ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Set your posting times</EmptyTitle>
            <EmptyDescription>
              Add a few weekly time-slots so queued posts publish automatically
              at the next open time.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={openSettings}>
              <SlidersHorizontal data-icon="inline-start" />
              Queue settings
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <>
          {/* 2. Paused banner */}
          {paused ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-600 text-sm dark:text-amber-300">
              <Pause className="size-4 shrink-0 fill-current" />
              <p>
                <strong className="font-semibold">Queue paused.</strong> Posts
                stay put and nothing publishes until you resume.
              </p>
            </div>
          ) : null}

          {/* 2b. Unpublishable posts (channel disconnected / needs reauth) */}
          {blockedCount > 0 ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive text-sm">
              <PlugZap className="size-4 shrink-0" />
              <p>
                <strong className="font-semibold">
                  {blockedCount} queued {blockedCount === 1 ? "post" : "posts"}{" "}
                  can&apos;t be published.
                </strong>{" "}
                {blockedIssue === "disconnected"
                  ? "Their channel is no longer connected"
                  : "Their channel needs to be reconnected"}
                — reconnect it, or remove the posts from the queue to keep them
                as drafts.
              </p>
            </div>
          ) : null}

          {/* 3. Next up hero */}
          {nextUpRow && nextUpRow.kind === "filled" ? (
            <NextUpHero
              post={nextUpRow.post}
              ms={nextUpRow.ms}
              tz={tz}
              paused={paused}
              pinned={nextUpRow.pinned}
              category={
                nextUpRow.post.categoryId
                  ? categoryMeta.get(nextUpRow.post.categoryId)
                  : undefined
              }
              onSkip={() =>
                skipOccurrence(
                  nextUpRow.ms,
                  nextUpRow.post.gridAccountId ?? null,
                )
              }
              onReturnToQueue={() => returnToQueue(nextUpRow.post)}
              onEdit={() => editPost(nextUpRow.post)}
            />
          ) : null}

          {/* 4. Timeline */}
          {days.length > 0 ? (
            <DndContext
              collisionDetection={dropCollisionDetection}
              onDragCancel={handleDragCancel}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              sensors={sensors}
            >
              <div className="flex flex-col gap-1.5">
                {days.map((group) => (
                  <div key={group.key} className="flex flex-col">
                    <div className="sticky top-0 z-10 flex items-baseline gap-2.5 bg-background pt-1.5 pb-2">
                      <span className="font-heading text-lg leading-none">
                        {group.label}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {group.date}
                      </span>
                      <span className="h-px flex-1 bg-border" />
                      <span className="text-muted-foreground text-xs">
                        {group.sub}
                      </span>
                    </div>

                    <ul className="flex flex-col">
                      {group.rows.map((row) => (
                        <SlotRow
                          key={row.key}
                          row={row}
                          tz={tz}
                          isNext={row.key === firstFilledKey}
                          category={
                            row.kind === "filled" && row.post.categoryId
                              ? categoryMeta.get(row.post.categoryId)
                              : undefined
                          }
                          onAddClick={() => {
                            if (row.kind === "open") {
                              openSlotComposer(row.ms);
                            }
                          }}
                          onEdit={editPost}
                          onSkip={skipOccurrence}
                          onRestore={restoreOccurrence}
                          onRemove={removePost}
                          onReturnToQueue={returnToQueue}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <DragOverlay dropAnimation={null}>
                {activePost ? (
                  <div style={{ width: active?.width || undefined }}>
                    <FilledCardPreview
                      post={activePost}
                      category={
                        activePost.categoryId
                          ? categoryMeta.get(activePost.categoryId)
                          : undefined
                      }
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Your queue is empty</EmptyTitle>
                <EmptyDescription>
                  Write a post and choose "Add to Queue" to fill the next open
                  slot.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button onClick={() => openComposer()}>
                  <PenLine data-icon="inline-start" />
                  New post
                </Button>
              </EmptyContent>
            </Empty>
          )}

          {/* 5. Tail hint */}
          {days.length > 0 ? (
            <div className="mt-1 flex items-center gap-2.5 rounded-xl border bg-card px-4 py-3.5">
              <Sparkles className="size-4 shrink-0 text-primary" />
              <span className="flex-1 text-muted-foreground text-xs">
                {tailNote}
              </span>
              <Button variant="outline" size="sm" onClick={openSettings}>
                Manage slots
              </Button>
            </div>
          ) : null}
        </>
      )}

      {/* 6. Settings slide-over */}
      <QueueSettingsSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        timezone={tz}
        scopeName={settingsScopeName}
        inherited={settingsInherited}
        onCustomize={() => {
          if (selectedChannelId) {
            customizeMutation.mutate({
              workspaceId,
              socialAccountId: selectedChannelId,
            });
          }
        }}
        customizePending={customizeMutation.isPending}
        slots={settingsSlots.data ?? []}
        slotsPending={settingsSlots.isLoading}
        categories={categoryList}
        categoryMeta={categoryMeta}
        slotDays={slotDays}
        slotTime={slotTime}
        onSlotDaysChange={setSlotDays}
        onSlotTimeChange={setSlotTime}
        categoryName={categoryName}
        onCategoryNameChange={setCategoryName}
        onAddSlot={() => {
          if (slotDays.length === 0) {
            return;
          }
          const [hours, minutes] = slotTime.split(":").map(Number);
          createSlot.mutate({
            workspaceId,
            socialAccountId: gridAccountId,
            daysOfWeek: slotDays,
            minuteOfDay: (hours ?? 0) * 60 + (minutes ?? 0),
            timezone: tz,
          });
        }}
        addSlotPending={createSlot.isPending}
        onDeleteSlot={(id) => deleteSlot.mutate({ workspaceId, id })}
        deletingSlotId={
          deleteSlot.isPending ? (deleteSlot.variables?.id ?? null) : null
        }
        onAddCategory={() => {
          const name = categoryName.trim();
          if (!name) {
            return;
          }
          createCategory.mutate({ workspaceId, name });
          setCategoryName("");
        }}
        addCategoryPending={createCategory.isPending}
        onArchiveCategory={(id) => archiveCategory.mutate({ workspaceId, id })}
        archiveCategoryPending={archiveCategory.isPending}
      />
    </div>
  );
}

// --- Next up hero -----------------------------------------------------------

function NextUpHero({
  post,
  ms,
  tz,
  paused,
  pinned,
  category,
  onSkip,
  onReturnToQueue,
  onEdit,
}: {
  post: QueuePost;
  ms: number;
  tz: string;
  paused: boolean;
  /** Pinned posts hold their custom time — skipping the slot can't move them. */
  pinned: boolean;
  category?: { name: string; color: string };
  onSkip: () => void;
  onReturnToQueue: () => void;
  onEdit: () => void;
}) {
  const heroChannel = post.channels[0] ?? null;
  const issue = channelIssue(post.channels);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/35 bg-gradient-to-br from-primary/10 to-card p-5 md:p-6">
      <div className="pointer-events-none absolute -top-16 -right-8 size-56 rounded-full bg-primary/25 blur-3xl" />
      <div className="relative flex flex-col gap-5 md:flex-row md:gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <div className="flex items-center gap-2">
            <span className="size-1.5 animate-pulse rounded-full bg-primary" />
            <span className="font-semibold text-primary text-xs uppercase tracking-wider">
              Next up
            </span>
          </div>
          <h2 className="font-heading text-2xl leading-tight md:text-[26px]">
            {post.title}
          </h2>
          {post.text ? (
            <p className="line-clamp-2 text-muted-foreground text-sm leading-relaxed">
              {post.text}
            </p>
          ) : null}
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {heroChannel ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground/[0.06] px-2.5 py-1 text-muted-foreground text-xs">
                <ProviderIcon
                  provider={heroChannel.provider}
                  size="sm"
                  className="rounded-md"
                />
                {channelSummaryLabel(post.channels)}
              </span>
            ) : null}
            {issue ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/15 px-2.5 py-1 text-destructive text-xs">
                <PlugZap className="size-3" />
                {CHANNEL_ISSUE_LABEL[issue]}
              </span>
            ) : null}
            {category ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground/[0.06] px-2.5 py-1 text-muted-foreground text-xs">
                <span
                  className="size-1.5 rounded-sm"
                  style={{ backgroundColor: category.color }}
                />
                {category.name}
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground/[0.06] px-2.5 py-1 text-muted-foreground text-xs">
              <Clock className="size-3" />
              {whenLabel(ms, tz)}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-end justify-between gap-3 md:flex-col">
          <div className="text-right">
            <div className="text-muted-foreground text-xs uppercase tracking-wide">
              Publishes
            </div>
            <div className="whitespace-nowrap font-heading text-3xl text-primary leading-tight">
              <Countdown targetMs={ms} paused={paused} />
            </div>
          </div>
          <div className="flex gap-2">
            {pinned ? (
              <Button variant="outline" size="sm" onClick={onReturnToQueue}>
                <PinOff data-icon="inline-start" />
                Return to queue
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={onSkip}>
                <SkipForward data-icon="inline-start" />
                Skip slot
              </Button>
            )}
            <Button size="sm" onClick={onEdit}>
              <PenLine data-icon="inline-start" />
              Edit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Live countdown; re-renders itself every second so the surrounding timeline
 *  doesn't. Shows "paused" when the queue is paused. */
function Countdown({
  targetMs,
  paused,
}: {
  targetMs: number;
  paused: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (paused) {
      return;
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [paused]);

  if (paused) {
    return <>paused</>;
  }

  const diff = targetMs - now;
  if (diff <= 0) {
    return <>now</>;
  }
  const d = Math.floor(diff / 86_400_000);
  const h = Math.floor(diff / 3_600_000) % 24;
  const m = Math.floor(diff / 60_000) % 60;
  const s = Math.floor(diff / 1000) % 60;
  if (d > 0) {
    return (
      <>
        {d}d {h}h
      </>
    );
  }
  if (h > 0) {
    return (
      <>
        {h}h {m}m
      </>
    );
  }
  return (
    <>
      {m}m {s}s
    </>
  );
}

// --- Timeline slot row ------------------------------------------------------

function SlotRow({
  row,
  tz,
  isNext,
  category,
  onAddClick,
  onEdit,
  onSkip,
  onRestore,
  onRemove,
  onReturnToQueue,
}: {
  row: TimelineRow;
  tz: string;
  isNext: boolean;
  category?: { name: string; color: string };
  onAddClick: () => void;
  onEdit: (post: QueuePost) => void;
  /** Skip must target the post's own grid — see `skipOccurrence`. */
  onSkip: (ms: number, gridAccountId: string | null) => void;
  onRestore: (ms: number) => void;
  onRemove: (post: QueuePost) => void;
  onReturnToQueue: (post: QueuePost) => void;
}) {
  const { time, ampm } = timeParts(row.ms, tz);

  // Every row is a drop target. Dropping on a FILLED card reorders; dropping on
  // an OPEN slot pins the post to that time. dnd-kit resolves drops by pointer,
  // so the whole row is valid; the payload carries the slot instant + kind.
  const { isOver, setNodeRef } = useDroppable({
    id: row.key,
    data: { rank: row.rank, ms: row.ms, kind: row.kind },
  });

  return (
    <li
      ref={setNodeRef}
      className="grid grid-cols-[46px_20px_1fr] items-stretch md:grid-cols-[66px_26px_1fr]"
    >
      {/* Time column */}
      <div className="flex flex-col items-end pt-3.5 pr-3">
        <span
          className={cn(
            "whitespace-nowrap font-semibold text-sm",
            isNext ? "text-primary" : "text-foreground/80",
          )}
        >
          {time}
        </span>
        <span className="text-[10px] text-muted-foreground">{ampm}</span>
      </div>

      {/* Rail column */}
      <div className="relative flex justify-center">
        <span className="absolute inset-y-0 w-0.5 bg-foreground/10" />
        <SlotNode kind={row.kind} />
      </div>

      {/* Content column */}
      <div className="min-w-0 py-1.5">
        {isOver ? (
          <div className="mb-2 h-0.5 rounded bg-primary shadow-[0_0_10px_var(--color-primary)]" />
        ) : null}

        {row.kind === "filled" ? (
          <FilledCard
            post={row.post}
            isNext={isNext}
            pinned={row.pinned}
            category={category}
            onEdit={() => onEdit(row.post)}
            onSkip={() => onSkip(row.ms, row.post.gridAccountId ?? null)}
            onRemove={() => onRemove(row.post)}
            onReturnToQueue={() => onReturnToQueue(row.post)}
          />
        ) : null}

        {row.kind === "open" ? (
          <button
            type="button"
            onClick={onAddClick}
            className="flex w-full items-center gap-2.5 rounded-xl border border-border border-dashed px-3.5 py-3 text-left font-medium text-muted-foreground text-sm transition-colors hover:border-primary/60 hover:bg-primary/5 hover:text-primary"
          >
            <Plus className="size-4" />
            Open slot — add a post
          </button>
        ) : null}

        {row.kind === "skipped" ? (
          <div className="flex items-center gap-2.5 rounded-xl border border-border border-dashed bg-foreground/[0.015] px-3.5 py-2.5">
            <XIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-muted-foreground text-xs">
              Slot skipped — nothing publishes here
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRestore(row.ms)}
            >
              Restore
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function SlotNode({ kind }: { kind: TimelineRow["kind"] }) {
  if (kind === "filled") {
    return (
      <span
        className="relative mt-4 size-2.5 rounded-full bg-primary"
        style={{
          boxShadow:
            "0 0 0 4px var(--color-background), 0 0 0 6px color-mix(in oklab, var(--color-primary) 20%, transparent)",
        }}
      />
    );
  }
  if (kind === "open") {
    return (
      <span
        className="relative mt-4 size-2.5 rounded-full border-2 border-muted-foreground/60 border-dashed bg-background"
        style={{ boxShadow: "0 0 0 3px var(--color-background)" }}
      />
    );
  }
  return (
    <span
      className="relative mt-4 size-2 rounded-full border-2 border-border bg-transparent"
      style={{ boxShadow: "0 0 0 4px var(--color-background)" }}
    />
  );
}

/** Shared card content (title, text, category + channel meta). Rendered by the
 *  interactive queue card and by the drag-overlay preview. The channel is the
 *  post's real target (from `post_targets`), so each card shows where it goes. */
function CardBody({
  post,
  category,
  onEdit,
}: {
  post: QueuePost;
  category?: { name: string; color: string };
  onEdit?: () => void;
}) {
  const channel = post.channels[0] ?? null;
  const issue = channelIssue(post.channels);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="truncate text-left font-semibold text-sm hover:underline"
        >
          {post.title}
        </button>
      ) : (
        <span className="truncate font-semibold text-sm">{post.title}</span>
      )}
      {post.text ? (
        <p className="line-clamp-2 text-muted-foreground text-xs leading-relaxed">
          {post.text}
        </p>
      ) : null}
      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
        {category ? (
          <>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="size-1.5 rounded-sm"
                style={{ backgroundColor: category.color }}
              />
              {category.name}
            </span>
            {channel ? (
              <span className="size-0.5 rounded-full bg-muted-foreground/50" />
            ) : null}
          </>
        ) : null}
        {channel ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <ProviderIcon
              provider={channel.provider}
              size="sm"
              className="shrink-0 rounded-md"
            />
            <span className="truncate">
              {channelSummaryLabel(post.channels)}
            </span>
          </span>
        ) : null}
        {issue ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/15 px-1.5 py-0.5 font-medium text-destructive">
            <PlugZap className="size-3" />
            {CHANNEL_ISSUE_LABEL[issue]}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function FilledCard({
  post,
  isNext,
  pinned,
  category,
  onEdit,
  onSkip,
  onRemove,
  onReturnToQueue,
}: {
  post: QueuePost;
  isNext: boolean;
  pinned: boolean;
  category?: { name: string; color: string };
  onEdit: () => void;
  onSkip: () => void;
  onRemove: () => void;
  onReturnToQueue: () => void;
}) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, isDragging } =
    useDraggable({ id: post.id });
  const [confirmRemove, setConfirmRemove] = useState(false);

  return (
    <>
      <div
        ref={setNodeRef}
        className={cn(
          "group/card flex items-start gap-2.5 rounded-xl border bg-card p-3 pl-2 transition",
          isNext && "border-primary/40 ring-1 ring-primary/15",
          pinned && "border-amber-500/40",
          isDragging && "opacity-40",
        )}
      >
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                ref={setActivatorNodeRef}
                type="button"
                className="flex cursor-grab touch-none items-center self-stretch rounded-sm pt-0.5 text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring active:cursor-grabbing"
                aria-label={`Reorder “${post.title}”`}
                {...attributes}
                {...listeners}
              />
            }
          >
            <GripVertical className="size-4" />
          </TooltipTrigger>
          <TooltipContent>
            Drag to reorder, or onto an open slot to pin a custom time
          </TooltipContent>
        </Tooltip>

        <CardBody post={post} category={category} onEdit={onEdit} />

        <div className="flex shrink-0 items-center gap-1.5">
          {pinned ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 font-medium text-[10px] text-amber-600 dark:text-amber-300">
              <Pin className="size-3 fill-current" />
              Pinned
            </span>
          ) : null}
          <div className="flex gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/card:opacity-100">
            {pinned ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Return to queue"
                      onClick={onReturnToQueue}
                    />
                  }
                >
                  <PinOff />
                </TooltipTrigger>
                <TooltipContent>
                  Return to the queue (clear custom time)
                </TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Skip this slot"
                      onClick={onSkip}
                    />
                  }
                >
                  <SkipForward />
                </TooltipTrigger>
                <TooltipContent>Skip this time slot</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove from queue"
                    className="hover:bg-destructive/15 hover:text-destructive"
                    onClick={() => setConfirmRemove(true)}
                  />
                }
              >
                <XIcon />
              </TooltipTrigger>
              <TooltipContent>Remove from queue</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from queue?</AlertDialogTitle>
            <AlertDialogDescription>
              “{post.title}” will move back to your Drafts — nothing is deleted,
              and you can re-add it to the queue anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep in queue</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onRemove();
                setConfirmRemove(false);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** A static copy of a queue card that rides the cursor while dragging. */
function FilledCardPreview({
  post,
  category,
}: {
  post: QueuePost;
  category?: { name: string; color: string };
}) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border bg-card p-3 pl-2 shadow-xl ring-1 ring-black/5">
      <span className="flex cursor-grabbing items-center self-stretch pt-0.5 text-muted-foreground">
        <GripVertical className="size-4" />
      </span>
      <CardBody post={post} category={category} />
    </div>
  );
}

/** Header channel switcher: pick "All channels" (the workspace-wide stream) or
 *  drill into one channel's own queue. Mirrors the workspace-switcher pattern. */
function ChannelSwitcher({
  accounts,
  selectedChannelId,
  onSelect,
}: {
  accounts: SocialAccount[];
  selectedChannelId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const selected = selectedChannelId
    ? (accounts.find((account) => account.id === selectedChannelId) ?? null)
    : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="outline"
            className="h-9 max-w-full shrink-0 gap-2.5 rounded-full py-0 pr-3 pl-1 font-medium sm:pr-4"
          />
        }
      >
        {selected ? (
          <ChannelAvatar
            name={accountName(selected)}
            avatarUrl={selected.avatarUrl}
            provider={selected.provider}
            size="md"
            className="-my-0.5 size-10 ring-2 ring-background"
          />
        ) : (
          <ChannelStack accounts={accounts} />
        )}
        <span className="min-w-0 max-w-32 truncate sm:max-w-40">
          {selected ? accountName(selected) : "All channels"}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuItem onClick={() => onSelect(null)}>
          <ChannelStack accounts={accounts} />
          <span className="flex-1 truncate font-medium">All channels</span>
          {selectedChannelId === null ? <Check data-icon="inline-end" /> : null}
        </DropdownMenuItem>
        {accounts.length > 0 ? <DropdownMenuSeparator /> : null}
        {accounts.map((account) => (
          <DropdownMenuItem
            key={account.id}
            onClick={() => onSelect(account.id)}
          >
            <ChannelAvatar
              name={accountName(account)}
              avatarUrl={account.avatarUrl}
              provider={account.provider}
              size="sm"
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-medium text-sm">
                {accountName(account)}
              </span>
              <span className="truncate text-muted-foreground text-xs">
                {getChannelSecondaryLabel(account) ??
                  getProviderLabel(account.provider)}
              </span>
            </span>
            {selectedChannelId === account.id ? (
              <Check data-icon="inline-end" />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Overlapping avatar stack shown for the "All channels" option. */
function ChannelStack({ accounts }: { accounts: SocialAccount[] }) {
  if (accounts.length === 0) {
    return (
      <span className="flex size-6 items-center justify-center rounded-full bg-muted text-[10px] text-muted-foreground">
        —
      </span>
    );
  }
  const shown = accounts.slice(0, 3);
  const extra = accounts.length - shown.length;
  return (
    <span className="flex items-center gap-1">
      {shown.map((account) => (
        <ChannelAvatar
          key={account.id}
          name={accountName(account)}
          avatarUrl={account.avatarUrl}
          provider={account.provider}
          size="sm"
          showBadge
          className="ring-2 ring-card"
        />
      ))}
      {extra > 0 ? (
        <span className="flex size-7 items-center justify-center rounded-lg bg-muted text-[10px] text-muted-foreground ring-2 ring-card">
          +{extra}
        </span>
      ) : null}
    </span>
  );
}

// --- Settings slide-over ----------------------------------------------------

function QueueSettingsSheet({
  open,
  onOpenChange,
  timezone: tz,
  scopeName,
  inherited,
  onCustomize,
  customizePending,
  slots,
  slotsPending,
  categories,
  categoryMeta,
  slotDays,
  slotTime,
  onSlotDaysChange,
  onSlotTimeChange,
  categoryName,
  onCategoryNameChange,
  onAddSlot,
  addSlotPending,
  onDeleteSlot,
  deletingSlotId,
  onAddCategory,
  addCategoryPending,
  onArchiveCategory,
  archiveCategoryPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timezone: string;
  scopeName: string | null;
  inherited: boolean;
  onCustomize: () => void;
  customizePending: boolean;
  slots: QueueSlot[];
  slotsPending: boolean;
  categories: QueueCategory[];
  categoryMeta: Map<string, { name: string; color: string }>;
  slotDays: number[];
  slotTime: string;
  onSlotDaysChange: (days: number[]) => void;
  onSlotTimeChange: (time: string) => void;
  categoryName: string;
  onCategoryNameChange: (name: string) => void;
  onAddSlot: () => void;
  addSlotPending: boolean;
  onDeleteSlot: (id: string) => void;
  deletingSlotId: string | null;
  onAddCategory: () => void;
  addCategoryPending: boolean;
  onArchiveCategory: (id: string) => void;
  archiveCategoryPending: boolean;
}) {
  const [hours, minutes] = slotTime.split(":").map(Number);
  const slotMinuteOfDay = (hours ?? 0) * 60 + (minutes ?? 0);
  const addLabel =
    slotDays.length === 0
      ? "Select days"
      : slotDays.length === 1
        ? `Add ${DAY_NAMES[slotDays[0]]} ${minuteLabel(slotMinuteOfDay)}`
        : `Add ${slotDays.length} times at ${minuteLabel(slotMinuteOfDay)}`;

  const slotsByDay = ALL_DAYS.flatMap((day) => {
    const daySlots = slots
      .filter((slot) => slot.dayOfWeek === day)
      .sort((a, b) => a.minuteOfDay - b.minuteOfDay);
    return daySlots.length > 0 ? [{ day, slots: daySlots }] : [];
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[92vw] gap-0 p-0 sm:w-[420px] sm:max-w-[420px]"
      >
        <SheetHeader className="border-b p-5">
          <SheetTitle className="font-heading text-xl">
            Queue settings
          </SheetTitle>
          <SheetDescription>
            {scopeName
              ? `Posting schedule for ${scopeName}`
              : "Default schedule — applies to channels that haven't set their own"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-5">
          {/* Posting slots */}
          <section className="flex flex-col gap-3">
            <div>
              <h3 className="font-semibold text-sm">Posting slots</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">
                {inherited
                  ? `This channel follows the workspace default schedule (${tz}).`
                  : scopeName
                    ? `Recurring weekly times for ${scopeName} (${tz}). New queued posts fill the next open slot.`
                    : `Default posting times (${tz}) — new channels start from these until customized.`}
              </p>
            </div>

            {inherited ? (
              <div className="flex flex-col gap-2.5 rounded-xl border border-dashed bg-muted/30 px-4 py-3.5">
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Following the default schedule. Customize to give this channel
                  its own posting times — other channels are unaffected.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="self-start"
                  disabled={customizePending}
                  onClick={onCustomize}
                >
                  {customizePending ? (
                    <>
                      <Spinner data-icon="inline-start" />
                      Customizing…
                    </>
                  ) : (
                    <>
                      <SlidersHorizontal data-icon="inline-start" />
                      Customize for this channel
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <form
                className="flex flex-col gap-3 rounded-xl border bg-muted/20 p-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  onAddSlot();
                }}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-muted-foreground text-xs">
                      Days
                    </span>
                    <div className="flex flex-wrap justify-end gap-1">
                      {(
                        [
                          ["Weekdays", WEEKDAY_DAYS],
                          ["Weekends", WEEKEND_DAYS],
                          ["Every day", ALL_DAYS],
                        ] as const
                      ).map(([label, days]) => {
                        const active = sameDaySet(slotDays, days);
                        return (
                          <Button
                            key={label}
                            type="button"
                            size="sm"
                            variant={active ? "secondary" : "ghost"}
                            className="h-6 px-2 text-[11px]"
                            aria-pressed={active}
                            onClick={() => onSlotDaysChange([...days])}
                          >
                            {label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  <fieldset className="m-0 flex justify-between gap-1 border-0 p-0">
                    <legend className="sr-only">Days of week</legend>
                    {DAY_LETTERS.map((letter, index) => {
                      const selected = slotDays.includes(index);
                      return (
                        <Toggle
                          key={`${DAY_NAMES[index]}-${letter}`}
                          size="sm"
                          variant="outline"
                          pressed={selected}
                          aria-label={DAY_NAMES[index]}
                          className={cn(
                            "size-9 shrink-0 p-0 font-semibold text-xs",
                            selected &&
                              "border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground",
                          )}
                          onPressedChange={(pressed) => {
                            onSlotDaysChange(
                              pressed
                                ? [...slotDays, index].sort((a, b) => a - b)
                                : slotDays.filter((day) => day !== index),
                            );
                          }}
                        >
                          {letter}
                        </Toggle>
                      );
                    })}
                  </fieldset>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="font-medium text-muted-foreground text-xs">
                    Time
                  </span>
                  <Input
                    type="time"
                    value={slotTime}
                    aria-label="Slot time"
                    className="cursor-pointer"
                    onChange={(event) =>
                      onSlotTimeChange(event.currentTarget.value)
                    }
                    onClick={(event) => {
                      try {
                        event.currentTarget.showPicker?.();
                      } catch {
                        // Some browsers reject showPicker when already open.
                      }
                    }}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={addSlotPending || slotDays.length === 0}
                >
                  {addSlotPending ? (
                    <>
                      <Spinner data-icon="inline-start" />
                      Adding…
                    </>
                  ) : (
                    <>
                      <Plus data-icon="inline-start" />
                      {addLabel}
                    </>
                  )}
                </Button>
              </form>
            )}

            {slotsPending ? (
              <Skeleton className="h-16 w-full" />
            ) : slotsByDay.length > 0 ? (
              <div className="flex flex-col gap-2.5">
                {slotsByDay.map(({ day, slots: daySlots }) => (
                  <div key={day} className="flex flex-col gap-1.5">
                    <span className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
                      {DAY_NAMES[day]}
                    </span>
                    <ul className="flex flex-wrap gap-1.5">
                      {daySlots.map((slot) => (
                        <li
                          key={slot.id}
                          className="flex items-center gap-1.5 rounded-lg border bg-card py-1 pr-1 pl-2.5 text-xs"
                        >
                          <Clock className="size-3 text-muted-foreground" />
                          <span className="font-medium">
                            {minuteLabel(slot.minuteOfDay)}
                          </span>
                          {inherited ? null : (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={
                                deletingSlotId === slot.id
                                  ? `Removing ${DAY_NAMES[day]} ${minuteLabel(slot.minuteOfDay)}`
                                  : `Delete ${DAY_NAMES[day]} ${minuteLabel(slot.minuteOfDay)}`
                              }
                              aria-busy={deletingSlotId === slot.id}
                              className="size-5 hover:bg-destructive/15 hover:text-destructive"
                              disabled={deletingSlotId !== null}
                              onClick={() => onDeleteSlot(slot.id)}
                            >
                              {deletingSlotId === slot.id ? (
                                <Spinner />
                              ) : (
                                <Trash2 />
                              )}
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">
                No slots yet. Pick days, choose a time, and add.
              </p>
            )}
          </section>

          <Separator />

          {/* Categories */}
          <section className="flex flex-col gap-3">
            <div>
              <h3 className="font-semibold text-sm">Categories</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">
                Optional content buckets to organize your queue.
              </p>
            </div>

            <form
              className="flex items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                onAddCategory();
              }}
            >
              <Input
                value={categoryName}
                placeholder="e.g. Tips"
                aria-label="Category name"
                className="flex-1"
                onChange={(event) =>
                  onCategoryNameChange(event.currentTarget.value)
                }
              />
              <Button type="submit" disabled={addCategoryPending}>
                {addCategoryPending ? (
                  <>
                    <Spinner data-icon="inline-start" />
                    Adding
                  </>
                ) : (
                  "Add"
                )}
              </Button>
            </form>

            {categories.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <li
                    key={category.id}
                    className="flex items-center gap-1.5 rounded-lg border bg-card py-1 pr-1 pl-2.5 text-xs"
                  >
                    <span
                      className="size-2 rounded-sm"
                      style={{
                        backgroundColor: categoryMeta.get(category.id)?.color,
                      }}
                    />
                    <span className="font-medium">{category.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Archive category"
                      className="size-5 hover:bg-destructive/15 hover:text-destructive"
                      disabled={archiveCategoryPending}
                      onClick={() => onArchiveCategory(category.id)}
                    >
                      <Trash2 />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-xs">
                No categories yet.
              </p>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// --- Loading skeleton -------------------------------------------------------

/** Body-only skeleton (hero + timeline). The toolbar renders above it always,
 *  so switching channels keeps the header put and skeletons only the body. */
function QueueBodySkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <Skeleton className="h-36 w-full rounded-2xl" />
      <div className="flex flex-col gap-3">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    </div>
  );
}
